import React, { useState, useEffect } from 'react';
import { doc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';

const PayPeriods = ({ db }) => {
    const [viewYear, setViewYear] = useState(new Date().getFullYear());
    const [periods, setPeriods] = useState({}); // Map of monthIndex -> { startDate, payday, processDate, cutoffDate, message }
    const [loading, setLoading] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    // --- DEFAULT LOGIC HELPERS ---
    
    // Calculate the 'Period End' (Cutoff) for a given month
    const getCalculatedPeriodEnd = (year, month) => {
        let paydayDate = new Date(year, month, 23);
        const dayOfWeek = paydayDate.getDay(); // 0=Sun, ... 6=Sat

        let cutoffDate = new Date(paydayDate);

        if (dayOfWeek === 6) { // Sat
             cutoffDate.setDate(20);
        } else if (dayOfWeek === 0) { // Sun
             cutoffDate.setDate(19);
        } else if (dayOfWeek === 1) { // Mon
             cutoffDate.setDate(19); 
        } else {
             cutoffDate.setDate(21);
        }
        return cutoffDate;
    };

    // Calculate Payday and Process Date
    const getProcessAndPayDate = (year, month) => {
        let paydayDate = new Date(year, month, 23);
        const dayOfWeek = paydayDate.getDay(); 

        let processDate = new Date(paydayDate);

        if (dayOfWeek === 6) { // Sat
             paydayDate.setDate(22);
             processDate.setDate(21);
        } else if (dayOfWeek === 0) { // Sun
             paydayDate.setDate(21);
             processDate.setDate(20);
        } else if (dayOfWeek === 1) { // Mon
             processDate.setDate(20); // Fri
        } else {
             processDate.setDate(22);
        }

        return {
            payday: paydayDate.toISOString().split('T')[0],
            processDate: processDate.toISOString().split('T')[0]
        };
    };

    // Load saved periods or calculate defaults using chaining
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const q = query(collection(db, "pay_periods"), where("year", "==", viewYear));
                const snapshot = await getDocs(q);
                
                const loaded = {};
                snapshot.forEach(doc => {
                    const data = doc.data();
                    loaded[data.month] = data;
                });

                const fullList = {};
                
                // We need to track the end of the previous month to calculate the start of the current month.
                // For January (m=0), we look at December of the previous year.
                let prevPeriodEnd = getCalculatedPeriodEnd(viewYear - 1, 11);

                for (let m = 0; m < 12; m++) {
                    if (loaded[m]) {
                        // Use loaded data
                        fullList[m] = loaded[m];
                        
                        // Update tracker for next iteration
                        if (loaded[m].cutoffDate) {
                            prevPeriodEnd = new Date(loaded[m].cutoffDate);
                        } else {
                            // Fallback if corrupt data
                            prevPeriodEnd = getCalculatedPeriodEnd(viewYear, m);
                        }
                    } else {
                        // Calculate Default
                        const periodEndDateObj = getCalculatedPeriodEnd(viewYear, m);
                        const { payday, processDate } = getProcessAndPayDate(viewYear, m);
                        
                        // Start Date is Day After Previous End
                        const startDateObj = new Date(prevPeriodEnd);
                        startDateObj.setDate(startDateObj.getDate() + 1);

                        const newPeriod = {
                            year: viewYear,
                            month: m,
                            startDate: startDateObj.toISOString().split('T')[0],
                            cutoffDate: periodEndDateObj.toISOString().split('T')[0],
                            payday,
                            processDate
                        };
                        
                        fullList[m] = newPeriod;
                        
                        // Update tracker
                        prevPeriodEnd = periodEndDateObj;
                    }
                }
                setPeriods(fullList);
            } catch (e) {
                console.error("Error loading pay periods:", e);
            }
            setLoading(false);
        };
        loadData();
    }, [db, viewYear]);

    const handleUpdate = (month, field, value) => {
        setPeriods(prev => ({
            ...prev,
            [month]: { ...prev[month], [field]: value }
        }));
    };

    const handleSave = async (month) => {
        const data = periods[month];
        const docId = `period_${viewYear}_${month}`;
        await setDoc(doc(db, "pay_periods", docId), data, { merge: true });
        setSaveMessage(`Saved ${monthNames[month]}`);
        setTimeout(() => setSaveMessage(''), 3000);
    };

    const getOrdinalDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const d = date.getDate();
        const m = monthNames[date.getMonth()];
        const suffix = (d % 10 === 1 && d !== 11) ? "st" : (d % 10 === 2 && d !== 12) ? "nd" : (d % 10 === 3 && d !== 13) ? "rd" : "th";
        return `${d}${suffix} ${m}`;
    };

    return (
        <div className="card" style={{maxWidth:'900px', margin:'0 auto'}}>
            <div className="flex-between" style={{marginBottom:'20px'}}>
                <h3>Pay Period Configuration</h3>
                <select value={viewYear} onChange={e=>setViewYear(Number(e.target.value))} style={{padding:'5px'}}>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            </div>
            
            {loading && <p style={{textAlign:'center', color:'var(--text-muted)'}}>Loading configuration...</p>}
            {saveMessage && <div style={{textAlign:'center', color:'var(--success)', fontWeight:'bold', marginBottom:'10px'}}>{saveMessage}</div>}

            <div style={{display:'grid', gap:'15px'}}>
                {Object.keys(periods).map(key => {
                    const m = Number(key);
                    const p = periods[m];
                    const message = `Payday: ${getOrdinalDate(p.payday)}. Shifts up to ${getOrdinalDate(p.cutoffDate)} must be submitted by 9am ${getOrdinalDate(p.processDate)}.`;
                    
                    // --- GAP CHECK ---
                    let dateWarning = null;
                    if (m > 0) {
                        const prevPeriod = periods[m - 1];
                        if (prevPeriod && prevPeriod.cutoffDate && p.startDate) {
                            const prevCutoff = new Date(prevPeriod.cutoffDate);
                            const currentStart = new Date(p.startDate);
                            const diffTime = currentStart - prevCutoff;
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            
                            // Expect exactly 1 day difference (e.g. Cutoff 21st, Start 22nd)
                            if (diffDays !== 1) {
                                dateWarning = `⚠️ Warning: Dates do not align! Previous period ended ${getOrdinalDate(prevPeriod.cutoffDate)}, but this period starts ${getOrdinalDate(p.startDate)}. There are ${Math.abs(diffDays - 1)} days missing or overlapping.`;
                            }
                        }
                    }

                    return (
                        <div key={m} style={{background:'var(--light)', padding:'15px', borderRadius:'8px', border:'1px solid var(--border)'}}>
                            <div className="flex-between" style={{marginBottom:'10px'}}>
                                <h4 style={{margin:0}}>{monthNames[m]}</h4>
                                <button onClick={()=>handleSave(m)} style={{background:'var(--success)', color:'white', padding:'5px 10px', fontSize:'12px'}}>Save</button>
                            </div>
                            
                            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'10px', marginBottom:'10px'}}>
                                <div>
                                    <label style={{fontSize:'11px', display:'block', fontWeight:'bold'}}>Period Start</label>
                                    <input type="date" value={p.startDate} onChange={e=>handleUpdate(m, 'startDate', e.target.value)} style={{width:'100%'}} />
                                </div>
                                <div>
                                    <label style={{fontSize:'11px', display:'block', fontWeight:'bold'}}>Period End</label>
                                    <input type="date" value={p.cutoffDate} onChange={e=>handleUpdate(m, 'cutoffDate', e.target.value)} style={{width:'100%'}} />
                                </div>
                                <div>
                                    <label style={{fontSize:'11px', display:'block', fontWeight:'bold'}}>Process Date (9am)</label>
                                    <input type="date" value={p.processDate} onChange={e=>handleUpdate(m, 'processDate', e.target.value)} style={{width:'100%'}} />
                                </div>
                                <div>
                                    <label style={{fontSize:'11px', display:'block', fontWeight:'bold'}}>Payday</label>
                                    <input type="date" value={p.payday} onChange={e=>handleUpdate(m, 'payday', e.target.value)} style={{width:'100%'}} />
                                </div>
                            </div>
                            
                            {dateWarning && (
                                <div style={{fontSize:'12px', color:'#991b1b', background:'rgba(179,38,30,0.15)', padding:'8px', borderRadius:'4px', marginBottom:'10px', border:'1px solid #f87171'}}>
                                    {dateWarning}
                                </div>
                            )}

                            <div style={{fontSize:'11px', color:'#a5b0f0', background:'rgba(94,106,210,0.15)', padding:'5px', borderRadius:'4px'}}>
                                <strong>Generated Message:</strong> {message}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PayPeriods;